import React, { useEffect, useReducer, useRef } from "react";
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { useSectionEditor } from "./SectionEditorContext";

const createPrompt = (index = 0) => ({
  id: `prompt-${index}`,
  text: "",
  expectedResponse: "",
});

export const createInitialMediaState = (metadata = {}) => ({
  sourceType: metadata.sourceType || "upload",
  fileName: metadata.fileName || "",
  url: metadata.url || "",
  transcript: metadata.transcript || "",
  prompts: Array.isArray(metadata.prompts)
    ? metadata.prompts.map((prompt, index) => ({
        ...createPrompt(index),
        ...(prompt || {}),
      }))
    : [],
});

export const mediaPromptReducer = (state, action) => {
  switch (action.type) {
    case "HYDRATE": {
      if (!action.payload || typeof action.payload !== "object") {
        return state;
      }
      const nextState = createInitialMediaState(action.payload);
      const currentSerialized = JSON.stringify(state);
      const nextSerialized = JSON.stringify(nextState);
      return currentSerialized === nextSerialized ? state : nextState;
    }
    case "SET_SOURCE_TYPE":
      return {
        ...state,
        sourceType: action.value === "url" ? "url" : "upload",
        url: action.value === "url" ? state.url : "",
        fileName: action.value === "upload" ? state.fileName : "",
      };
    case "SET_FILE":
      return {
        ...state,
        fileName: action.value || "",
      };
    case "SET_URL":
      return {
        ...state,
        url: action.value || "",
      };
    case "SET_TRANSCRIPT":
      return { ...state, transcript: action.value || "" };
    case "ADD_PROMPT": {
      return {
        ...state,
        prompts: [...state.prompts, createPrompt(state.prompts.length)],
      };
    }
    case "REMOVE_PROMPT": {
      if (action.index == null || action.index < 0) {
        return state;
      }
      return {
        ...state,
        prompts: state.prompts.filter((_, index) => index !== action.index),
      };
    }
    case "UPDATE_PROMPT": {
      const { index, updates } = action;
      if (
        index == null ||
        index < 0 ||
        index >= state.prompts.length ||
        !updates ||
        typeof updates !== "object"
      ) {
        return state;
      }
      return {
        ...state,
        prompts: state.prompts.map((prompt, idx) =>
          idx === index ? { ...prompt, ...updates } : prompt
        ),
      };
    }
    default:
      return state;
  }
};

const computeMediaErrors = (state) => {
  const errors = {};
  if (state.sourceType === "upload" && !state.fileName) {
    errors.source = "Select a media file to upload.";
  }
  if (state.sourceType === "url" && (!state.url || !state.url.trim())) {
    errors.source = "Provide a media URL.";
  }

  const promptErrors = state.prompts.map((prompt) => {
    const itemErrors = {};
    if (!prompt.text || !prompt.text.trim()) {
      itemErrors.text = "Enter the question prompt.";
    }
    return itemErrors;
  });

  if (promptErrors.some((error) => Object.keys(error).length > 0)) {
    errors.promptErrors = promptErrors;
  }

  return Object.keys(errors).length ? errors : null;
};

const MediaPromptSectionEditor = () => {
  const { section, updateMetadata, setSectionErrors, sectionErrors } =
    useSectionEditor();
  const [state, dispatch] = useReducer(
    mediaPromptReducer,
    section.metadata,
    createInitialMediaState
  );
  const serializedRef = useRef(JSON.stringify(state));
  const lastErrorRef = useRef(null);

  useEffect(() => {
    dispatch({ type: "HYDRATE", payload: section.metadata });
  }, [section.metadata]);

  useEffect(() => {
    const serialized = JSON.stringify(state);
    if (serializedRef.current !== serialized) {
      serializedRef.current = serialized;
      updateMetadata({
        sourceType: state.sourceType,
        fileName: state.fileName,
        url: state.url,
        transcript: state.transcript,
        prompts: state.prompts,
      });
    }
  }, [state, updateMetadata]);

  useEffect(() => {
    const errors = computeMediaErrors(state);
    const serializedErrors = JSON.stringify(errors || {});
    if (serializedErrors !== lastErrorRef.current) {
      lastErrorRef.current = serializedErrors;
      setSectionErrors(errors);
    }
  }, [state, setSectionErrors]);

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    dispatch({ type: "SET_FILE", value: file ? file.name : "" });
  };

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h6" component="h3">
            Media prompt
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Attach an audio or video resource and pair it with reflective
            questions.
          </Typography>
        </Box>

        <FormControl component="fieldset">
          <Typography variant="subtitle2" gutterBottom>
            Media source
          </Typography>
          <RadioGroup
            row
            value={state.sourceType}
            onChange={(event) =>
              dispatch({ type: "SET_SOURCE_TYPE", value: event.target.value })
            }
          >
            <FormControlLabel value="upload" control={<Radio />} label="Upload" />
            <FormControlLabel value="url" control={<Radio />} label="URL" />
          </RadioGroup>
        </FormControl>

        {state.sourceType === "upload" ? (
          <Stack spacing={1}>
            <Button variant="outlined" component="label">
              {state.fileName ? `Replace file (${state.fileName})` : "Choose file"}
              <input type="file" hidden onChange={handleFileChange} />
            </Button>
            {sectionErrors?.source && (
              <Typography color="error" variant="body2">
                {sectionErrors.source}
              </Typography>
            )}
          </Stack>
        ) : (
          <TextField
            label="Media URL"
            value={state.url}
            onChange={(event) =>
              dispatch({ type: "SET_URL", value: event.target.value })
            }
            helperText={sectionErrors?.source}
            error={Boolean(sectionErrors?.source)}
          />
        )}

        <TextField
          label="Transcript / accessibility notes"
          value={state.transcript}
          onChange={(event) =>
            dispatch({ type: "SET_TRANSCRIPT", value: event.target.value })
          }
          multiline
          minRows={3}
        />

        <Stack spacing={2}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="subtitle2">Associated questions</Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => dispatch({ type: "ADD_PROMPT" })}
            >
              Add question
            </Button>
          </Stack>
          {state.prompts.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Add questions for learners to consider while engaging with the media.
            </Typography>
          ) : (
            <Stack spacing={1.5}>
              {state.prompts.map((prompt, index) => (
                <Paper
                  key={prompt.id || index}
                  variant="outlined"
                  sx={{ p: 2, bgcolor: "background.default" }}
                >
                  <Stack spacing={1.5}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="subtitle2">
                        Question {index + 1}
                      </Typography>
                      <IconButton
                        aria-label={`Remove question ${index + 1}`}
                        onClick={() =>
                          dispatch({ type: "REMOVE_PROMPT", index })
                        }
                        size="small"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={7}>
                        <TextField
                          label="Question prompt"
                          value={prompt.text || ""}
                          onChange={(event) =>
                            dispatch({
                              type: "UPDATE_PROMPT",
                              index,
                              updates: { text: event.target.value },
                            })
                          }
                          fullWidth
                          helperText={
                            sectionErrors?.promptErrors?.[index]?.text
                          }
                          error={Boolean(
                            sectionErrors?.promptErrors?.[index]?.text
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} md={5}>
                        <TextField
                          label="Expected response / notes"
                          value={prompt.expectedResponse || ""}
                          onChange={(event) =>
                            dispatch({
                              type: "UPDATE_PROMPT",
                              index,
                              updates: {
                                expectedResponse: event.target.value,
                              },
                            })
                          }
                          fullWidth
                        />
                      </Grid>
                    </Grid>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          )}
        </Stack>
      </Stack>
    </Paper>
  );
};

export default MediaPromptSectionEditor;
